import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { faUserFriends, faUsersCog, faUserCheck, faBuilding, faBullhorn, faClipboardList, faCalendarCheck, faTicketAlt } from "@fortawesome/free-solid-svg-icons";
import ReactApexChart from "react-apexcharts";
import apiServices from "../../../../services/apiService";
import storageService from "../../../../utils/storageService";
import encryptedDataService from "../../../../services/encryptedDataService";
import MetricCard from "../../../common/Card/MetricCard";
import ActionCenter from "../../../common/ActionCenter/ActionCenter";

const Dashboard = () => {
  const { profileData } = useSelector((state) => state.profile);
  const [metricsData, setMetricsData] = useState([
    { title: 'Today Visitors', value: 0, icon: faUserFriends, color: 'text-indigo-500' },
    { title: 'Total Staff', value: 0, icon: faUsersCog, color: 'text-indigo-500' },
    { title: 'Total Members', value: 0, icon: faUserCheck, color: 'text-indigo-500' },
    { title: 'Total Wings', value: 0, icon: faBuilding, color: 'text-indigo-500' }
  ]);

  const [lineChartOptions] = useState({
        chart: {
            toolbar: { show: false },
            fontFamily: "inherit"
        },
        stroke: {
            curve: "smooth",
            width: 3
        },
        dataLabels: {
            enabled: false
        },
        colors: ["#6366f1"],
        fill: {
            type: "solid",
            opacity: 0.1
        },
        grid: {
            borderColor: "#f3f4f6",
            strokeDashArray: 4
        },
        xaxis: {
            categories: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
            labels: { style: { colors: "#9ca3af" } },
            axisBorder: { show: false },
            axisTicks: { show: false }
        },
        yaxis: {
            labels: { style: { colors: "#9ca3af" } }
        },
        legend: {
            position: "top",
            horizontalAlign: "left"
        },
        tooltip: {
            theme: "light",
            intersect: false,
        }
  });

  const [lineChartSeries] = useState([
    { name: 'Visitors', data: [30, 45, 40, 50, 35, 60, 105] }
  ]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const encryptedEmployeeCode = storageService.getItem('employeeCode');
      if (!encryptedEmployeeCode) return;
      
      const userId = encryptedDataService.decryptData(encryptedEmployeeCode);
      
      // Step 1: Get user site
      const userRes = await apiServices({
        method: "POST",
        url: "/ResidentialUser/GetResidentialUserById",
        data: { userId: userId }
      });

      if (userRes?.success && userRes?.data?.data?.length > 0) {
        const siteId = userRes.data.data[0].siteId;

        // Step 2: Get Dashboard Data
        const dashRes = await apiServices({
          method: "GET",
          url: `/ResidentialDashboard/GetDashboardDataBySiteId/${siteId}`
        });

        if (dashRes?.success && dashRes?.data?.IsSuccess && dashRes?.data?.data?.length > 0) {
          const d = dashRes.data.data[0];
          setMetricsData([
            { title: 'Today Visitors', value: d.VisitorsTotal || 0, icon: faUserFriends, color: 'text-indigo-500' },
            { title: 'Total Staff', value: d.StaffsTotal || 0, icon: faUsersCog, color: 'text-indigo-500' },
            { title: 'Total Members', value: d.MembersTotal || 0, icon: faUserCheck, color: 'text-indigo-500' },
            { title: 'Total Wings', value: d.WingsTotal || 0, icon: faBuilding, color: 'text-indigo-500' }
          ]);
        }
      }
    } catch (error) {
      console.error("Dashboard fetch error:", error);
    }
  };

  const currentDate = new Date();
  const currentHour = currentDate.getHours();
  const greeting =
      currentHour >= 5 && currentHour < 12
          ? "Good Morning"
          : currentHour >= 12 && currentHour < 17
              ? "Good Afternoon"
              : currentHour >= 17 && currentHour < 21
                  ? "Good Evening"
                  : "Good Night";

  const formattedDate = currentDate.toLocaleString("en-US", {
      weekday: "long",
      day: "2-digit",
      month: "short",
      year: "numeric"
  });

  const dashboardActions = [
      {
          icon: faBullhorn,
          title: "Notices",
          description: "Manage community notices",
          url: "/zentres/Notices",
          iconStyles: { bg: "bg-indigo-100", color: "text-indigo-600" }
      },
      {
          icon: faClipboardList,
          title: "Polls",
          description: "Create and view community polls",
          url: "/zentres/Poll-Management",
          iconStyles: { bg: "bg-blue-100", color: "text-blue-600" }
      },
      {
          icon: faCalendarCheck,
          title: "Amenities",
          description: "Check and manage amenity bookings",
          url: "/zentres/Amenities-Management/Amenities-Booking",
          iconStyles: { bg: "bg-green-100", color: "text-green-600" }
      },
      {
          icon: faTicketAlt,
          title: "Complaints",
          description: "Resolve helpdesk issues",
          url: "/zentres/ComplaintList",
          iconStyles: { bg: "bg-red-100", color: "text-red-600" }
      }
  ];

  return (
        <div className="bg-[#FFFFFF] min-h-screen rounded-2xl p-4 lg:p-6 overflow-y-auto scrollbar-hide">
            {/* WELCOME SECTION */}
            <div>
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mt-2">
                    <div>
                        <p className="text-sm text-slate-500 mb-4">
                            Dashboard
                        </p>

                        <h2 className="text-2xl font-bold text-slate-800">
                            {greeting},
                            <span className="text-red-500 ml-2">
                                {profileData?.firstName} {profileData?.lastName} 👋
                            </span>
                        </h2>

                        <p className="text-sm text-slate-500 mt-1">
                            {formattedDate} • Platform running smoothly • All systems operational
                        </p>
                    </div>
                </div>
            </div>

            {/* METRIC CARDS */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
                {metricsData.map((metric, index) => (
                    <MetricCard key={index} {...metric} />
                ))}
            </div>

            {/* CHARTS ROW */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mt-6">

                {/* Traffic Trends Area Chart */}
                <div className="xl:col-span-2 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                    <div className="mb-4">
                        <h2 className="text-lg font-bold text-slate-800">Traffic Trends</h2>
                        <p className="text-sm text-slate-400">Visitor frequency over last 7 days</p>
                    </div>
                    <ReactApexChart
                        options={lineChartOptions}
                        series={lineChartSeries}
                        type="area"
                        height={260}
                    />
                </div>

                {/* Action Center */}
                <div className="">
                    <ActionCenter
                        title="Quick Actions"
                        description="Frequently used residential actions"
                        actions={dashboardActions}
                        className="h-full"
                    />
                </div>

            </div>
        </div>
  );
};

export default Dashboard;
